const { geocodeAddress } = require('./mapsApi');
const {
  getDefaultCustomCoordinates,
  parseCountryCode,
} = require('./countriesUtils');
const { default: getFrontConfig } = require('../getFrontConfig');
const { memoizeAsync } = require('./fp');

/**
 * @typedef {Object} Coordinates
 * @property {number} lat - Latitude.
 * @property {number} lng - Longitude.
 */

/**
 * Returns the current geolocation position as a Promise.
 * @param {PositionOptions} [options] - Optional geolocation options.
 * @param {boolean} [options.enableHighAccuracy=false] - Use GPS if available
 *   (higher battery usage).
 * @param {number} [options.timeout=10000] - Max time (ms) to wait for a position.
 * @param {number} [options.maximumAge=0] - Max age (ms) of a cached position.
 * @returns {Promise<Coordinates>} Resolves with the coordinates, rejects with
 *   a GeolocationPositionError.
 * @throws {Error} If the Geolocation API is not supported by the browser.
 */
function getCurrentPositionAsync(options) {
  if (!navigator.geolocation) {
    return Promise.reject(
      new Error('Geolocation is not supported by this browser.')
    );
  }
  const defaults = { enableHighAccuracy: false, timeout: 10000, maximumAge: 0 };
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        }),
      reject,
      Object.assign(defaults, options)
    );
  });
}

/**
 * Returns the geolocation permission state without prompting the user, so
 * pages can decide what to offer before asking for the position.
 * @returns {Promise<PermissionState|'unsupported'>} Resolves with 'granted',
 *   'prompt' or 'denied'; 'prompt' when the Permissions API cannot tell, and
 *   'unsupported' when the browser has no Geolocation API. Never rejects.
 */
function getPermissionStateAsync() {
  if (!navigator.geolocation) {
    return Promise.resolve('unsupported');
  }
  if (!navigator.permissions?.query) {
    return Promise.resolve('prompt');
  }
  return navigator.permissions
    .query({ name: 'geolocation' })
    .then((status) => status.state)
    .catch(() => 'prompt');
}

/**
 * Returns the default position of the current site's country based on
 * front config. Uses custom configured coordinates if available, otherwise
 * geocodes the country name. Result is memoized for the session lifetime.
 * @returns {Promise<Coordinates>} Resolves with the country position,
 *   rejects if unresolvable.
 */
const getSiteCountryDefaultPosition = memoizeAsync(async () => {
  const { countryCode, mapApiKey } = getFrontConfig();
  const customCountryCoordinates = getDefaultCustomCoordinates(countryCode);
  let position;
  if (customCountryCoordinates) {
    position = customCountryCoordinates;
  } else {
    const country = parseCountryCode(countryCode);
    position = await geocodeAddress('', country, mapApiKey);
  }
  if (!position) {
    throw new Error(
      `Could not resolve coordinates for country: ${countryCode}`
    );
  }
  return { lat: position.lat, lng: position.lng };
});

module.exports = {
  geocodeAddress,
  getCurrentPositionAsync,
  getPermissionStateAsync,
  getSiteCountryDefaultPosition,
};
